// src/services/api.js
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request-Interceptor, um Token automatisch einzufügen
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('Token aus localStorage:', token ? 'Vorhanden' : 'Nicht vorhanden');
    
    // Wichtig: Immer den Authorization Header setzen, wenn ein Token vorhanden ist
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // WICHTIG: Für Pfade, die PDF oder Datei-Downloads betreffen,
      // kann der Token als Query-Parameter hinzugefügt werden
      if (config.url && (config.url.includes('/pdf') || config.url.includes('/download'))) {
        if (!config.params) {
          config.params = {};
        }
        config.params.token = token;
      }
    }
    
    return config;
  },
  (error) => {
    console.log('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response-Interceptor für Fehlerbehandlung (z.B. bei abgelaufenem Token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('API Error:', error);
    
    // Nur in die Konsole loggen, NICHT automatisch umleiten
    if (error.response) {
      console.log('Response Status:', error.response.status);
      console.log('Response Data:', error.response.data);
    }
    
    // WICHTIG: Keine automatische Umleitung bei 401-Fehlern!
    // Lassen Sie die Komponente selbst entscheiden, wie mit dem Fehler umgegangen wird
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me')
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`)
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getByCustomer: (customerId) => api.get(`/projects/customer/${customerId}`)
};

// Materials API
export const materialsAPI = {
  getAll: () => api.get('/materials'),
  getById: (id) => api.get(`/materials/${id}`),
  create: (data) => api.post('/materials', data),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
  // Methode zum Protokollieren der Materialnutzung
  logMaterialUsage: (data) => api.post('/materials/usage', data)
};

// Time Entries API
export const timeEntriesAPI = {
  getAll: () => api.get('/time-entries'),
  getByUser: () => {
    console.log('Token für Zeiterfassungen:', localStorage.getItem('token'));
    return api.get('/time-entries/user/me');
  },
  start: (data) => api.post('/time-entries/start', data),
  stop: (id, data) => api.post(`/time-entries/stop/${id}`, data),
  // Neue Methode für manuelle Zeiteinträge
  create: (data) => api.post('/time-entries', data),
  // Angepasste Methoden für die Rapport-Bestätigung mit korrekten Pfaden
  getByProject: (projectId) => api.get(`/time-entries/project/${projectId}`),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`)
};

// Material Usages API - Pfade an die vorhandenen Backend-Routen angepasst
export const materialUsagesAPI = {
  getAll: () => api.get('/materials/usage'),
  getById: (id) => api.get(`/materials/usage/${id}`),
  getByProject: (projectId) => api.get(`/materials/usage/project/${projectId}`),
  create: (data) => api.post('/materials/usage', data),
  update: (id, data) => api.put(`/materials/usage/${id}`, data),
  delete: (id) => api.delete(`/materials/usage/${id}`)
};

// Reports API
export const reportsAPI = {
  getAll: () => api.get('/reports'),
  getById: (id) => api.get(`/reports/${id}`),
  create: (data) => api.post('/reports', data),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
  // Methoden für die erweiterten Report-Funktionen
  signReport: (id, data) => api.post(`/reports/${id}/sign`, data),
  sendReport: (id) => api.post(`/reports/${id}/send`),
  getReportsByProject: (projectId) => api.get(`/reports/project/${projectId}`),
  getReportsByCustomer: (customerId) => api.get(`/reports/customer/${customerId}`),
  generatePDF: (id) => api.get(`/reports/${id}/pdf`, { responseType: 'blob' }),
  // Neue Methoden für die Bearbeitung von Zeiterfassungen und Material in Rapporten
  updateTimeEntries: (id, data) => api.put(`/reports/${id}/time-entries`, data),
  updateMaterials: (id, data) => api.put(`/reports/${id}/materials`, data),
  // Neue Methode für die Aktualisierung des Reparaturstatus
  updateRepairStatus: (id, data) => api.patch(`/reports/${id}/repair-status`, data)
};

// Offers API
export const offersAPI = {
  getAll: () => api.get('/offers'),
  getById: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
  // Offertenpositionen
  addItem: (offerId, data) => api.post(`/offers/${offerId}/items`, data),
  updateItem: (offerId, itemId, data) => api.put(`/offers/${offerId}/items/${itemId}`, data),
  deleteItem: (offerId, itemId) => api.delete(`/offers/${offerId}/items/${itemId}`),
  // Spezielle Aktionen
  sendByEmail: (id) => api.post(`/offers/${id}/send`, {}),
  updateStatus: (id, status) => api.put(`/offers/${id}/status`, { status }),
  getByCustomer: (customerId) => api.get(`/offers/customer/${customerId}`),
  // PDF-Generierung und Vorschau
  generatePDF: (id) => api.get(`/offers/${id}/pdf`, { 
    responseType: 'blob',
    headers: {
      'Accept': 'application/pdf'
    }
  }),
  
  // PDF per E-Mail versenden
  sendPDFByEmail: (id, emailData) => api.post(`/offers/${id}/send-pdf`, emailData)
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

// Services (Dienstleistungen) API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`)
};

// Invoices API
export const invoicesAPI = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  createManual: (data) => api.post('/invoices/manual', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getByReport: (reportId) => api.get(`/invoices/report/${reportId}`),
  getByCustomer: (customerId) => api.get(`/invoices/customer/${customerId}`),
  markAsPaid: (id, data) => api.post(`/invoices/${id}/paid`, data),
  reopenInvoice: (id) => api.post(`/invoices/${id}/reopen`),
  createReminder: (id, data) => api.post(`/invoices/${id}/reminder`, data),
  // Neue Methode zum Versenden der Rechnung per E-Mail
  sendByEmail: (id, emailData) => api.post(`/invoices/${id}/email`, emailData),
  generatePDF: (id) => api.get(`/invoices/${id}/pdf`, {
    responseType: 'blob',
    // Debug-Info für die Anfrage hinzufügen
    headers: {
      'Accept': 'application/pdf'
    }
  })
};

// CompanyProfile API - Pfad korrigiert von /company-profile auf /company-profiles
export const companyProfileAPI = {
  getProfile: () => api.get('/company-profiles'),
  createProfile: (data) => {
    // Für FormData den Content-Type vom Browser setzen lassen
    const config = {
      headers: {
        'Content-Type': undefined
      }
    };
    return api.post('/company-profiles', data, config);
  },
  updateProfile: (data) => {
    // Für FormData den Content-Type vom Browser setzen lassen
    const config = {
      headers: {
        'Content-Type': undefined
      }
    };
    return api.put('/company-profiles', data, config);
  },
  deleteLogo: () => api.delete('/company-profiles/logo')
};

export default api;
